import type { Service, MarketplaceStatus } from '@/prisma/client'

// Tipo per i dati del marketplace con le relazioni incluse (come restituiti dall'API)
type MarketplaceWithRelations = {
  id: number
  createdAt: string
  updatedAt: string
  activityId: number
  categoryId: number | null
  order: number
  catalogId: number | null
  serviceId: number
  fee: number
  status: MarketplaceStatus
  source: string
  activity: {
    id: number
    name: string
    logo: string | null
    userId: number
    subdomain: string
  }
  service: {
    id: number
    name: string
    activity: {
      id: number
      name: string
      logo: string | null
      userId: number
      subdomain: string
    }
  }
  catalog: {
    id: number
    name: string
  } | null
}

interface MarketplaceCatalog {
  id: number
  name: string
  images: string[]
  public: boolean
  description: string | null
  activity: {
    id: number
    subdomain: string
    name: string
    city: string
    description: string | null
    images: string[]
    logo: string | null
    activityType: {
      id: number
      name: string
    } | null
  }
  _count: {
    services: number
  }
}

interface MarketplaceService {
  id: number
  name: string
  price: any // Prisma Decimal
  maxResaleFee: number | null
  description: string
  images: string[]
  category: {
    name: string
  } | null
  activity: {
    id: number
    subdomain: string
    name: string
    city: string
    logo: string | null
    activityType: {
      id: number
      name: string
    } | null
  }
  catalogs: {
    catalog: {
      id: number
      name: string
    }
  }[]
}

interface MarketplaceSearchResponse {
  catalogs: MarketplaceCatalog[]
  services: MarketplaceService[]
}

export const useMarketplaceStore = defineStore('marketplace', () => {
  const userContents = ref()
  const networkContents = ref<MarketplaceSearchResponse>()
  const loadingUserContents = ref(false)
  const loadingNetworkContents = ref(false)
  const cityList = ref()
  const loadingCityList = ref(false)
  const categories = ref()
  const loadingCategories = ref(false)
  const services = ref<Service[]>([])
  const items = ref<MarketplaceWithRelations[]>([])
  const pending = ref(0)
  async function fetchUserContents() {
    try {
      loadingUserContents.value = true
      userContents.value = await $fetch('/api/marketplace/user')
      return { data: userContents.value, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: userContents.value, status: 'error', error: e.data || e }
    } finally {
      loadingUserContents.value = false
    }
  }
  async function fetch() {
    try {
      items.value = await $fetch<MarketplaceWithRelations[]>('/api/marketplace')
      return { data: items.value, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: services.value, status: 'error', error: e.data || e }
    }
  }
  async function fetchPending() {
    try {
      pending.value = await $fetch<number>('/api/marketplace/pending')
      return { data: pending.value, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: services.value, status: 'error', error: e.data || e }
    }
  }
  async function fetchNetworkContents(filters: any = {}) {
    try {
      loadingNetworkContents.value = true
      networkContents.value = await $fetch<MarketplaceSearchResponse>('/api/marketplace/search', {
        method: 'POST',
        body: filters,
      })
      return { data: networkContents.value, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: networkContents.value, status: 'error', error: e.data || e }
    } finally {
      loadingNetworkContents.value = false
    }
  }
  async function fetchCityList() {
    try {
      loadingCityList.value = true
      cityList.value = await $fetch('/api/marketplace/city-list')
      return { data: cityList.value, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: cityList.value, status: 'error', error: e.data || e }
    } finally {
      loadingCityList.value = false
    }
  }
  async function fetchCategories() {
    try {
      loadingCategories.value = true
      categories.value = await $fetch('/api/marketplace/categories')
      return { data: categories.value, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: categories.value, status: 'error', error: e.data || e }
    } finally {
      loadingCategories.value = false
    }
  }
  async function addService(params: { serviceId: number, activityId: number, catalogId: number, source: 'DIRECT' | 'PROPOSAL' }) {
    try {
      const result = await $fetch('/api/marketplace/service', {
        method: 'POST',
        body: params,
      })
      if (!result.error) {
        services.value.push({
          ...result.service,
          marketplaceId: result.id,
          order: result.order,
        })
        return { data: result, status: 'success', error: null }
      } else {
        return { data: null, status: 'error', error: result.error }
      }
    } catch (e: unknown) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }
  async function fetchServices(catalogId: number) {
    try {
      services.value = (await $fetch('/api/marketplace/service', { query: { catalogId } })).map(
        (connection: any) => ({
          ...connection.service,
          marketplaceId: connection.id,
          order: connection.order,
        }),
      )
      return { data: services.value, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: services.value, status: 'error', error: e.data || e }
    }
  }
  async function updateOrder() { // save in db the actual order of services
    try {
      const result = await $fetch(`/api/marketplace/order`, {
        method: 'POST',
        body: {
          services: services.value?.map((service: any) => ({ id: service.marketplaceId, order: service.order })),
        },
      })
      return { data: result, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }
  async function updateStatus(id: number, status: MarketplaceStatus) {
    try {
      const result = await $fetch(`/api/marketplace/status`, {
        method: 'POST',
        body: {
          id, status,
        },
      })
      if (status !== 'ACCEPTED') {
        const index = services.value?.findIndex(s => s.marketplaceId === id)
        if (index !== -1) services.value?.splice(index, 1)
      }
      const item = items.value.find(item => item.id === id)
      if (item) item.status = status
      fetchPending()
      return { data: result, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }
  async function remove(id: number) {
    try {
      const result = await $fetch('/api/marketplace', {
        method: 'DELETE',
        query: { id },
      })
      if (result) {
        const index = items.value?.findIndex(s => s.id === id)
        if (index !== -1) {
          items.value?.splice(index, 1)
        }
      }
      return { data: result, status: 'success', error: null }
    } catch (e: unknown) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }
  return {
    fetch,
    fetchServices,
    fetchUserContents,
    fetchNetworkContents,
    fetchCityList,
    fetchCategories,
    fetchPending,
    addService,
    updateOrder,
    updateStatus,
    remove,
    items,
    pending,
    services,
    userContents,
    networkContents,
    cityList,
    categories,
    loadingUserContents,
    loadingNetworkContents,
    loadingCityList,
    loadingCategories,
  }
})
